/**
 * Web house-ad backfill using Sellwild listings.
 *
 * When a paid creative is absent (GPT isEmpty / PrebidOnly no-fill), fill the
 * slot with a listing from the widget feed (photo + product URL). Works for
 * any slot size — MREC gets a full card; skinny banners get a compact strip.
 * Cleared automatically when a paid creative fills.
 *
 * Kill switch: HOUSE_AD_ENABLED (default on).
 */

import { ICustomizations, IListing } from '../providers/types'
import { getListingUrl } from '../providers/Customizations'

export type HouseAdImpression = {
  slotId: string
  kind: 'listing'
  size: string
  listingId: string
}

type HouseAdState = {
  listings: IListing[]
  config: any
  pending: Set<string>
  impressed: Set<string>
  /** Slot element IDs created by Sellwild (widget / sellwild-ad only). */
  ownedSlots: Set<string>
  /** Stable pick index per slot id, assigned in markup order. */
  ordinals: {[slotId: string]: number}
  nextOrdinal: number
  /** Feed entries consumed by organic grid cells; house ads start past them. */
  gridListingCount: number
  listenerInstalled: boolean
  themeForRetry: ICustomizations | null
  fillOptions: FillHouseAdOptions
}

const state: HouseAdState = {
  listings: [],
  config: null,
  pending: new Set(),
  impressed: new Set(),
  ownedSlots: new Set(),
  ordinals: {},
  nextOrdinal: 0,
  gridListingCount: 0,
  listenerInstalled: false,
  themeForRetry: null,
  fillOptions: {},
}

const HOUSE_ATTR = 'data-sellwild-house-ad'

/** Register a GPT/Prebid slot div id so house ads only fill our inventory. */
export function registerHouseAdSlot(slotId: string) {
  if (!slotId) return
  state.ownedSlots.add(slotId)
  slotOrdinal(slotId)
}

function isSellwildOwnedSlot(slotEl: HTMLElement): boolean {
  if (state.ownedSlots.has(slotEl.id)) return true
  // Belt-and-suspenders: never touch publisher slots outside our markup.
  return !!(
    slotEl.closest('sellwild-widget') ||
    slotEl.closest('sellwild-ad') ||
    slotEl.closest('.sellwild-listing-ad') ||
    slotEl.closest('.sellwild-sticky-bottom-container')
  )
}

function parseBool(raw: unknown, defaultValue: boolean): boolean {
  if (raw === undefined || raw === null || raw === '') return defaultValue
  if (typeof raw === 'boolean') return raw
  if (typeof raw === 'number') return raw !== 0
  const s = String(raw).toLowerCase()
  if (['0', 'false', 'no', 'off'].includes(s)) return false
  if (['1', 'true', 'yes', 'on'].includes(s)) return true
  return defaultValue
}

export function parseSlotSize(slotEl: HTMLElement): { w: number; h: number } | null {
  const fromId = slotEl.id && slotEl.id.match(/(\d+)x(\d+)/)
  if (fromId) return { w: Number(fromId[1]), h: Number(fromId[2]) }
  const w = parseInt(slotEl.style.width, 10)
  const h = parseInt(slotEl.style.height, 10)
  if (w > 0 && h > 0) return { w, h }
  return null
}

function hasUsablePhoto(listing: IListing): boolean {
  return !!(listing && listing.photos && listing.photos[0] && listing.photos[0].url)
}

/**
 * Pick a listing for a slot, skipping ids already painted on the page.
 *
 * Indexes into the raw feed rather than a photo-filtered copy so the index
 * space matches the organic grid's `listings[i % listings.length]`; listings
 * without a usable photo are skipped during the walk instead.
 */
export function pickListing(
  listings: IListing[],
  startIndex: number,
  excludeIds?: Set<string>
): IListing | null {
  const total = listings.length
  if (!total) return null
  const start = ((startIndex % total) + total) % total
  let firstWithPhoto: IListing | null = null

  for (let i = 0; i < total; i++) {
    const listing = listings[(start + i) % total]
    if (!listing || !hasUsablePhoto(listing)) continue
    if (!excludeIds || !excludeIds.has(String(listing.id))) return listing
    if (!firstWithPhoto) firstWithPhoto = listing
  }

  // Feed exhausted: a duplicate with a photo still beats a photoless card.
  return firstWithPhoto || listings[start] || null
}

/**
 * Stable per-slot pick index, assigned in markup order at registration.
 * Slot ids carry a counter scoped per size (`...-300x250-0`, `...-320x50-0`)
 * or a label (`...-sticky-bottom`), so the id alone can't order slots.
 */
function slotOrdinal(slotId: string): number {
  if (state.ordinals[slotId] === undefined) {
    state.ordinals[slotId] = state.nextOrdinal++
  }
  return state.ordinals[slotId]
}

/** Topmost house-ad wrapper above a node, if it sits inside one. */
function houseCreativeRoot(node: HTMLElement): HTMLElement | null {
  let found: HTMLElement | null = null
  let el: HTMLElement | null = node
  while (el) {
    if (el.hasAttribute && el.hasAttribute(HOUSE_ATTR)) found = el
    el = el.parentElement
  }
  return found
}

/** True for creatives that mounting into this slot is about to remove. */
function isOwnHouseCreative(node: HTMLElement, slotEl: HTMLElement): boolean {
  const root = houseCreativeRoot(node)
  if (!root) return false
  if (slotEl.contains(root)) return true
  // Legacy parent-host overlays that clearHouseAd() also removes.
  return root.parentElement === slotEl.parentElement
}

/**
 * Listing ids already on the page. Organic grid cards and other slots' house
 * creatives both carry `.sellwild-listing[data-id]`, so one query covers both.
 * This slot's own creative is skipped so a refresh keeps its current listing
 * instead of rotating under the reader.
 */
function collectRenderedListingIds(slotEl: HTMLElement): Set<string> {
  const ids = new Set<string>()
  if (typeof document === 'undefined') return ids
  const nodes = document.querySelectorAll('.sellwild-listing[data-id]')
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i] as HTMLElement
    if (isOwnHouseCreative(node, slotEl)) continue
    const id = node.getAttribute('data-id')
    if (id) ids.add(String(id))
  }
  return ids
}

export type SetHouseListingsOptions = {
  /** Grid cells painted from the head of this feed. */
  gridListingCount?: number
}

export function setHouseListings(
  listings: IListing[],
  config: any,
  options: SetHouseListingsOptions = {}
) {
  state.listings = listings || []
  state.config = config
  // Widget and sellwild-ad mounts share this module. Keep the largest
  // reservation so a standalone tag (no grid cells) can't undo a grid's.
  if (options.gridListingCount) {
    state.gridListingCount = Math.max(state.gridListingCount, options.gridListingCount)
  }
  if (!state.themeForRetry || !state.pending.size) return

  const pending = Array.from(state.pending)
  state.pending.clear()
  const theme = state.themeForRetry
  for (const slotId of pending) {
    const el = document.getElementById(slotId) as HTMLElement | null
    if (!el) continue
    fillHouseAd(el, theme, state.fillOptions)
  }
}

export function clearHouseAd(slotEl: HTMLElement) {
  const nodes = slotEl.querySelectorAll(`[${HOUSE_ATTR}]`)
  for (let i = 0; i < nodes.length; i++) nodes[i].remove()
  // Also clear sibling overlays from older mounts (parent-host approach).
  const parent = slotEl.parentElement
  if (parent) {
    const siblings = parent.querySelectorAll(`:scope > [${HOUSE_ATTR}]`)
    for (let i = 0; i < siblings.length; i++) siblings[i].remove()
  }
  slotEl.style.visibility = ''
  state.pending.delete(slotEl.id)
  // Impression keys survive on purpose: a refresh that no-fills again re-mounts
  // the same listing, and that re-mount is not a new impression.
}

/**
 * Mount house creative inside the slot div itself.
 * PrebidOnly owns this div (no GPT). GPT no-fill leaves it empty — same mount works.
 * Avoid parent overlays: partner CSS (e.g. TOI height:auto + aspect-ratio) can
 * collapse absolute siblings.
 */
function mountHouseOverlay(slotEl: HTMLElement, node: HTMLElement, size: { w: number; h: number }) {
  clearHouseAd(slotEl)
  const pos = window.getComputedStyle(slotEl).position
  if (pos === 'static' || !pos) {
    slotEl.style.position = 'relative'
  }
  // Keep a definite box so partner height:auto rules still size via aspect-ratio / inline size.
  if (!slotEl.style.width) slotEl.style.width = `${size.w}px`
  if (!slotEl.style.height) slotEl.style.height = `${size.h}px`

  const wrap = document.createElement('div')
  wrap.setAttribute(HOUSE_ATTR, '1')
  wrap.style.cssText = [
    'position:absolute',
    'inset:0',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'z-index:2',
    'overflow:hidden',
    'width:100%',
    'height:100%',
  ].join(';')
  // Fill the slot; ignore fixed px on the card so aspect-ratio containers work.
  node.style.width = '100%'
  node.style.height = '100%'
  node.style.maxWidth = '100%'
  node.style.maxHeight = '100%'
  wrap.appendChild(node)
  slotEl.appendChild(wrap)
  slotEl.style.visibility = ''
}

function buildListingNode(
  listing: IListing,
  href: string,
  size: { w: number; h: number }
): HTMLElement {
  const photo = listing.photos && listing.photos[0]
  const src = (photo && photo.url) || ''
  const title = listing.title || ''

  // Wide/short slots (728x90, 320x50): full-bleed photo + title strip.
  // Tall slots (300x250): classic listing card.
  const isStrip = size.h < 120 || size.w / Math.max(size.h, 1) >= 4

  const a = document.createElement('a')
  a.className = 'sellwild-listing sellwild-house-ad sellwild-house-ad-listing'
  a.setAttribute(HOUSE_ATTR, 'listing')
  a.setAttribute('data-id', listing.id)
  a.target = '_blank'
  a.rel = 'nofollow sponsored noopener'
  a.href = href
  a.style.cssText = [
    'display:block',
    'width:100%',
    'height:100%',
    'margin:0',
    'overflow:hidden',
    'position:relative',
    'text-decoration:none',
    'color:inherit',
    'background:#1a1a1a',
  ].join(';')

  if (src) {
    const img = document.createElement('img')
    img.className = 'listing-img'
    img.src = src
    img.alt = title
    // Always cover the full slot — no letterboxed thumbs in leaderboards.
    img.style.cssText = [
      'position:absolute',
      'inset:0',
      'width:100%',
      'height:100%',
      'object-fit:cover',
      'object-position:center',
      'display:block',
      'border:0',
    ].join(';')
    a.appendChild(img)
  }

  // Use house-ad-specific classes — not listing-overlay / listing-title.
  // Overlay publishers (e.g. Genius) style those for hover-only centered
  // titles, which hid captions, forced single-line ellipsis, and cut text.
  const caption = document.createElement('div')
  caption.className = 'sellwild-house-ad-caption'
  if (isStrip) {
    caption.style.cssText = [
      'position:absolute',
      'left:0',
      'right:0',
      'bottom:0',
      'top:0',
      'display:flex',
      'align-items:center',
      'padding:0 16px',
      'background:linear-gradient(90deg,rgba(0,0,0,0.72) 0%,rgba(0,0,0,0.35) 45%,transparent 75%)',
      'color:#fff',
      'font:700 14px/1.25 system-ui,sans-serif',
      'z-index:2',
      'box-sizing:border-box',
      'opacity:1',
      'visibility:visible',
      'pointer-events:none',
    ].join(';')
  } else {
    caption.style.cssText = [
      'position:absolute',
      'left:0',
      'right:0',
      'bottom:0',
      'top:auto',
      'padding:8px 10px',
      'background:linear-gradient(transparent,rgba(0,0,0,0.65))',
      'color:#fff',
      'font:600 13px/1.3 system-ui,sans-serif',
      'z-index:2',
      'box-sizing:border-box',
      'opacity:1',
      'visibility:visible',
      'pointer-events:none',
    ].join(';')
  }
  const p = document.createElement('p')
  p.className = 'sellwild-house-ad-title'
  p.textContent = title
  p.style.cssText = isStrip
    ? [
        'margin:0',
        'max-width:55%',
        'overflow:hidden',
        'text-overflow:ellipsis',
        'white-space:nowrap',
        'color:#fff',
        'font:inherit',
      ].join(';')
    : [
        'margin:0',
        'overflow:hidden',
        'color:#fff',
        'font:inherit',
        'white-space:normal',
        'word-break:break-word',
        'display:-webkit-box',
        '-webkit-box-orient:vertical',
        '-webkit-line-clamp:2',
        'line-clamp:2',
      ].join(';')
  caption.appendChild(p)
  a.appendChild(caption)

  return a
}

export type FillHouseAdOptions = {
  onImpression?: (event: HouseAdImpression) => void
}

/**
 * Fill an empty ad slot with a listing. Safe to call repeatedly.
 * Returns true when something was shown.
 */
export function fillHouseAd(
  slotEl: HTMLElement,
  theme: ICustomizations,
  options: FillHouseAdOptions = {}
): boolean {
  if (!slotEl || !parseBool(theme.houseAdEnabled, true)) return false
  // Never backfill publisher / third-party GPT slots on the page.
  if (!isSellwildOwnedSlot(slotEl)) return false

  const size = parseSlotSize(slotEl)
  if (!size) return false
  // Leaderboards don't get listing backfill for now — a single listing looks
  // sparse in 728x90. MREC / 320x50 still fill.
  if (size.w === 728 && size.h === 90) return false

  state.themeForRetry = theme
  state.fillOptions = options

  if (!state.listings.length) {
    state.pending.add(slotEl.id)
    return false
  }

  const startIndex = state.gridListingCount + slotOrdinal(slotEl.id)
  const listing = pickListing(state.listings, startIndex, collectRenderedListingIds(slotEl))
  if (!listing) return false

  const href = getListingUrl(listing, state.config, theme)
  mountHouseOverlay(slotEl, buildListingNode(listing, href, size), size)

  const impressionKey = `${slotEl.id}:listing:${listing.id}`
  if (!state.impressed.has(impressionKey) && options.onImpression) {
    state.impressed.add(impressionKey)
    options.onImpression({
      slotId: slotEl.id,
      kind: 'listing',
      size: `${size.w}x${size.h}`,
      listingId: listing.id,
    })
  }

  return true
}

/**
 * Install GPT slotRenderEnded listener for house-ad backfill.
 * Only used when GAM/GPT owns the slot (adType Prebid / GTag — not PrebidOnly).
 * PrebidOnly fills via fillHouseAd() at the end of renderDirectAd instead.
 */
export function installHouseAdGptListener(
  getTheme: () => ICustomizations,
  options: FillHouseAdOptions = {}
) {
  if (state.listenerInstalled) return
  if (typeof window === 'undefined') return

  const theme = getTheme()
  // PrebidOnly never defines GPT slots — googletag.cmd never drains without GPT.
  if ((theme.adType || '') === 'PrebidOnly' && !(theme.standaloneAdType && theme.standaloneAdType !== 'PrebidOnly')) {
    return
  }

  state.listenerInstalled = true
  state.fillOptions = options
  window.googletag = window.googletag || { cmd: [] }
  window.googletag.cmd = window.googletag.cmd || []
  window.googletag.cmd.push(function () {
    try {
      window.googletag.pubads().addEventListener('slotRenderEnded', function (event: any) {
        const slotId = event && event.slot && event.slot.getSlotElementId && event.slot.getSlotElementId()
        if (!slotId || !state.ownedSlots.has(slotId)) return
        const el = document.getElementById(slotId) as HTMLElement | null
        if (!el) return
        const latestTheme = getTheme()
        if (event.isEmpty) {
          fillHouseAd(el, latestTheme, options)
        } else {
          clearHouseAd(el)
        }
      })
    } catch (err) {
      console.log('House ad GPT listener error', err)
    }
  })
}
